import React, { FormEvent, useEffect, useState } from 'react'
import { Form } from 'react-router-dom';
import { Alert, Button, IconButton, InputLabel, MenuItem, Select, styled, TextField, Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { IShipmentExpense, IShipmentExpensePayee, IShipmentLight } from '../../types/shipment';
import { IExpense } from '../../types/expense';
import { IManifest } from '../../types/manifest';

import { AppConfig } from '../../config/appConfig';
import { useListContext } from '../../contexts/listContext';
import { ShipmentService } from '../../services/shipmentService';
import { UtilityService } from '../../services/utilityService';

import { Decimal } from '../../decimal';
import Spinner from '../spinner';
import { Icons } from '../icons';
import NumberField from '../shared/numberField';

import '../../styles/shipmentExpensesForm.scss';

export interface IShipmentExpensesForm {
	expenses: IExpense[];
	isLoadingExpenses: boolean;
	shipmentDetails: IShipmentLight;
	isSpShipment: boolean | undefined;
	shipmentExpenses: IShipmentExpense[];
	isLoadingShipmentExpenses: boolean;
	manifests: IManifest[];
	isLoadingManifests: boolean;
	payees: IShipmentExpensePayee[];
	isLoadingPayees: boolean;
	isLoadingManifestWarnings: boolean;
	onExpensesUpdated?: (expenses: IShipmentExpense[]) => void;
	onManifestWarningsReloadRequest?: () => void;
}

const AlternateDescriptionTooltip = styled(({ className, ...props }: TooltipProps) => (
	<Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
	[`& .${tooltipClasses.arrow}`]: {
		color: theme.palette.common.black,

	},
	[`& .${tooltipClasses.tooltip}`]: {
		maxWidth: 350,
		fontSize: theme.typography.pxToRem(14),
		backgroundColor: theme.palette.common.black,
		color: theme.palette.common.white,
	},
}));

const ShipmentExpensesForm: React.FunctionComponent<IShipmentExpensesForm> = ({ expenses, isLoadingExpenses, shipmentDetails, isSpShipment, shipmentExpenses, isLoadingShipmentExpenses, manifests, isLoadingManifests, payees, isLoadingPayees, isLoadingManifestWarnings, onExpensesUpdated, onManifestWarningsReloadRequest }) => {
	const listContext = useListContext();
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [isRemovingId, setIsRemovingId] = useState<number | undefined>();
	const [isAdding, setIsAdding] = useState(false);
	const [total, setTotal] = useState(0);
	const [workingExpenses, setWorkingExpenses] = useState<IExpense[] | undefined>(undefined);
	const [filteredExpenses, setFilteredExpenses] = useState<IExpense[]>([]);
	const [filteredExpensesCountIgnoringSp, setFilteredExpensesCountIgnoringSp] = useState(0);
	const [expenseIdField, setExpenseIdField] = useState<string | undefined>(undefined);
	const [expenseAmountField, setExpenseAmountField] = useState(0);
	const [expensePayeeIndexField, setExpensePayeeIndexField] = useState<string | number>(-1);
	const [expenseAlternateDescriptionField, setExpenseAlternateDescriptionField] = useState<string | undefined>();
	const [expenseNotesField, setExpenseNotesField] = useState<string | undefined>();
	const [manifestNumberField, setManifestNumberField] = useState<string>("");
	const [selectedManifestId, setSelectedManifestId] = useState<string>('');
	const [isCostField, setIsCostField] = useState("1");
	const [showForm, setShowForm] = useState(true);
	const isBatchShipment = shipmentDetails.isBatched ?? false;
	const entityTypeVendorId = +listContext.entityTypes?.find(e => e.name == "Vendor")?.value;
	const entityTypeTransporterId = +listContext.entityTypes?.find(e => e.name == "Transporter")?.value;

	const precisionFull = AppConfig.precisionDecimalsMaximum;

	useEffect(() => {
		if (!isLoadingExpenses && Array.isArray(expenses)) {
			const workingExpenses = expenses.filter(e => e.isActive).sort((a, b) => UtilityService.compareTwoFields(a, b, 'category', 'name'));
			setWorkingExpenses(workingExpenses);
		}
	}, [isLoadingExpenses, expenses, payees])

	useEffect(() => {
		if (Array.isArray(shipmentExpenses) && Array.isArray(payees)) {
			shipmentExpenses.forEach(expense => {
				const payee = payees.find(p => p.id == expense.payeeId && p.type == expense.payeeType);
				let payeeName = payee?.name ?? `Unknown ${expense.payeeType} ID ${expense.payeeId}`;
				if (expense.payeeType) payeeName += ` (${expense.payeeType.substring(0, 1).toUpperCase()})`;
				expense.payeeName = payeeName;
			});
		}
	}, [shipmentExpenses, payees])

	useEffect(() => {
		if (filteredExpenses?.length) {
			setExpenseIdField(filteredExpenses[0].id.toString());
		}
		else {
			setExpenseIdField(undefined);
		}
	}, [filteredExpenses])

	useEffect(() => {
		if (shipmentExpenses) {
			setTotal(shipmentExpenses.reduce((acc, expense) => { return acc + (expense.isCost ? expense.amount * -1 : expense.amount) }, 0));
		}
	}, [shipmentExpenses])

	useEffect(() => {
		if (manifests?.length) {
			setSelectedManifestId('0');
		}
	}, [manifests])

	useEffect(() => {
		if (payees?.length) {
			setExpensePayeeIndexField(-1);
		}
	}, [payees])

	useEffect(() => {
		setFilteredExpenses([]);
		setFilteredExpensesCountIgnoringSp(0);

		if (+expensePayeeIndexField >= 0 && workingExpenses?.length) {
			const payee = payees[parseInt(expensePayeeIndexField as string)];
			console.log(payee);
			if (!payee) return;

			const entityTypeId = payee.type == "Vendor" ? entityTypeVendorId : payee.type == "Transporter" ? entityTypeTransporterId : undefined;
			if (!entityTypeId) return;

			let newFilteredExpenses =
				workingExpenses.filter(e =>
					e.assignedEntities?.some(a =>
						a.entityTypeId == entityTypeId && // Same entity type
						a.entityId == payee.id // Same payee							
					)
				);

			// Remember the count of normal filtered expenses before we filter out special project expenses
			setFilteredExpensesCountIgnoringSp(newFilteredExpenses.length);

			// If this is a special project shipment, filter out any expenses whose process is not "sp" (case insensitive)
			if (isSpShipment) {
				newFilteredExpenses = newFilteredExpenses.filter(e => e.process?.toLowerCase() === "sp");
			}

			setFilteredExpenses(newFilteredExpenses);
			setExpenseIdField(newFilteredExpenses.length ? newFilteredExpenses[0].id.toString() : undefined);
		}
	}, [expensePayeeIndexField, workingExpenses])

	const isFormValid = (): boolean => {
		if (+expensePayeeIndexField <= 0) {
			setErrorMessage("Payee is required.");
			return false;
		}

		if (!(+expenseIdField > 0)) {
			setErrorMessage("Expense is required.");
			return false;
		}

		return true;
	}

	const handleSubmitAdd = async (event: FormEvent<HTMLFormElement>) => {
		setErrorMessage(undefined);
		if (!isFormValid()) return;
		setIsAdding(true);

		const payee = payees[parseInt(expensePayeeIndexField as string)];
		let expense: IShipmentExpense = {
			shipmentId: parseInt(shipmentDetails.id.toString()),
			amount: expenseAmountField >= 0 ? expenseAmountField : expenseAmountField * -1,
			typeId: expenseIdField,
			notes: expenseNotesField,
			isCost: isCostField == "1" ? true : false,
			manifestId: selectedManifestId && selectedManifestId != '0' ? parseInt(selectedManifestId) || null : null,
			alternateDescription: expenseAlternateDescriptionField || null
		}

		if (expensePayeeIndexField != -1) {
			expense.payeeId = payee.id;
			expense.payeeName = payee.name;
			expense.payeeType = payee.type;
		}

		ShipmentService.addExpense(shipmentDetails.id.toString(), expense)
			.then((response) => {
				console.log('ShipmentService.addExpense: SUCCESS', response);
				setIsCostField('1');
				setExpenseNotesField(undefined);
				setExpenseAmountField(0);
				setExpenseIdField(workingExpenses[0].id.toString());
				setExpensePayeeIndexField(-1);
				setManifestNumberField("");
				setSelectedManifestId('0');
				onExpensesUpdated?.(response.data);
				onManifestWarningsReloadRequest?.();
			})
			.catch((error) => {
				console.log('ShipmentService.addExpense: ERROR', error);
				setErrorMessage("We encountered an error while processing this request. Please try again.");
			})
			.finally(() => {
				setIsAdding(false);
			});
	}

	const handleRemove = async (expenseId: number) => {
		setIsRemovingId(expenseId);

		ShipmentService.removeExpense(expenseId)
			.then((response) => {
				console.log('ShipmentService.removeExpense', response);
				onExpensesUpdated?.(response.data);
				onManifestWarningsReloadRequest?.();
			})
			.catch((error) => {
				console.log('ShipmentService.removeExpense', error);
				setErrorMessage("We encountered an error while processing this request. Please try again.");
			})
			.finally(() => {
				setIsRemovingId(undefined);
			});
	}

	const isDuplicatePayeeName = (name: string): boolean => {
		return name ? payees.filter(p => p.name.trim().toLowerCase() == name.trim().toLowerCase()).length > 1 : false;
	}

	return (
		<section className="hwi-shipment-expenses-form">
			<Spinner dependency={isLoadingShipmentExpenses || isLoadingPayees || isLoadingManifests || isLoadingManifestWarnings || isSpShipment === undefined} text="Loading expenses...">
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					{errorMessage &&
						<Alert className="alert-error" severity="error" action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>{errorMessage}</Alert>
					}
					{(isBatchShipment ? shipmentDetails?.batchStatus : shipmentDetails?.status) == "Reconciling" ? (
						<Form onSubmit={(event) => handleSubmitAdd(event)}>
							<div className="row header">
								<div className="form-title">Add Expense</div>
								<span onClick={() => setShowForm(!showForm)}>
									{showForm &&
										<Icons.Up fill="#272f50" height="32px"></Icons.Up>
									}
									{!showForm &&
										<Icons.Down fill="#272f50" height="32px"></Icons.Down>
									}
								</span>
							</div>
							{showForm &&
								<>
									<div className="row payee">
										<InputLabel>Payee *</InputLabel>
										<Select
											className={"type-field" + (errorMessage?.includes("Payee is required.") ? " error" : "")}
											value={expensePayeeIndexField}
											required
											fullWidth
											onChange={(event) => {
												setExpensePayeeIndexField(event.target.value);
											}}
										>
											<MenuItem value={-1} key={0}>Select one...</MenuItem>
											{payees.map((item, index) => (
												<MenuItem value={index} key={index}>{item.name} <span className="payee-type">({item.type})</span></MenuItem>
											))}
										</Select>
									</div>
									<div className="row expense">
										<InputLabel>Expense *{!workingExpenses && !isLoadingExpenses && <span className="loading-message"> (Loading...)</span>}</InputLabel>
										<Select
											className={"type-field" + (errorMessage?.includes("Expense is required.") ? " error" : "")}
											value={expenseIdField || ""}
											required
											disabled={isLoadingExpenses || (filteredExpenses?.length == 0)}
											fullWidth
											onChange={(event) => {
												setExpenseIdField(event.target.value);
											}}
										>
											{filteredExpenses?.map((item, index) => (
												<MenuItem value={item.id.toString()} key={index} >{item.category || 'N/A'} - {item.name}</MenuItem>
											))}
										</Select>
										{!isLoadingExpenses && filteredExpenses?.length == 0 && +expensePayeeIndexField >= 0 &&
											(isSpShipment && filteredExpensesCountIgnoringSp > 0 ? (
												<div className="no-assigned-expenses">Selected Payee has no allowed <strong>Special Project</strong> expense types.</div>
											) : (
												<div className="no-assigned-expenses">Selected Payee has no allowed expense types.</div>
											))
										}
									</div>
									<div className="row amount">
										<InputLabel>Amount *</InputLabel>
										<NumberField className="amount-field"
											value={expenseAmountField}
											required
											isCurrency={true}
											precision={precisionFull}
											fullWidth
											min={0}
											onChange={(event) => { setExpenseAmountField(parseFloat(event.target.value)) }}
										></NumberField>
									</div>
									<div className="row type">
										<InputLabel>Type *</InputLabel>
										<Select
											className="type-field"
											value={isCostField}
											required
											fullWidth
											onChange={(event) => {
												setIsCostField(event.target.value);
											}}
										>
											<MenuItem value="0" >Credit</MenuItem>
											<MenuItem value="1" >Cost</MenuItem>

										</Select>
									</div>
									<div className="row manifest">
										<InputLabel>Manifest</InputLabel>
										<Select
											className="manifest-field"
											value={selectedManifestId ?? '0'}
											fullWidth
											onChange={(event) => { setSelectedManifestId(event.target.value) }}
										>
											<MenuItem value='0'>None</MenuItem>
											{manifests.map((item, index) => (
												<MenuItem value={item.id} key={index} >{item.number}</MenuItem>
											))}
										</Select>
									</div>
									<div className="row alternate-description">
										<InputLabel>Alternate Expense Description</InputLabel>
										<AlternateDescriptionTooltip placement="top" title="If the expense description on the invoice is different than the expense name you selected, please indicate the original name here." arrow>
											<TextField className="alternate-description-field"
												value={expenseAlternateDescriptionField}
												fullWidth
												inputProps={{
													maxLength: 50,
												}}
												placeholder="Optional"
												onChange={(event) => { setExpenseAlternateDescriptionField(event.target.value) }}
											></TextField>
										</AlternateDescriptionTooltip>
									</div>
									<div className="row notes">
										<InputLabel>Notes</InputLabel>
										<TextField className="amount-field"
											value={expenseNotesField}
											fullWidth
											inputProps={{
												maxLength: 250,
											}}
											placeholder="Optional"
											onChange={(event) => { setExpenseNotesField(event.target.value) }}
										></TextField>
									</div>
									<div className="row controls">
										<Button variant="contained" type="submit" className="save-button" disabled={(isAdding || isRemovingId) ? true : false} >{isAdding ? "Adding..." : "Add Expense"}</Button>
									</div>
								</>
							}
						</Form>
					) : (
						<Alert className="alert-error" severity="info" >
							This shipment{isBatchShipment && <>'s batch</>} is in <strong>{isBatchShipment ? shipmentDetails?.batchStatus : shipmentDetails?.status}</strong> status. You can only add expenses to {isBatchShipment && <>batched</>} shipments{isBatchShipment && <> when their batch is</>} in <strong>Reconciling</strong> status.
						</Alert>
					)}
					<div className="expense-table">
						<div className="headers">
							<div className="col-expense"><span>Expense</span></div>
							<div className="col-amount">Amount</div>
							<div className="col-type">Type</div>
							<div className="col-payee">Payee</div>
							<div className='col-manifest-number'>Manifest #</div>
							<div className="col-notes">Notes</div>
							<div className="col-contorls"></div>
						</div>
						<div className="body">
							{shipmentExpenses.length > 0 ? (
								<>
									{shipmentExpenses.map((item, index) => (
										<div className="row" key={index}>
											<div className="col-expense"><span>{item.typeName}</span></div>
											<div className="col-amount">{new Decimal(item.amount ?? 0).toFixed(2)}</div>
											<div className="col-type">{item.isCost ? "Cost" : "Credit"}</div>
											<div className="col-payee">{item.payeeName}</div>
											<div className='col-manifest-number'>{item.manifestNumber || "-"}</div>
											<div className="col-notes">{item.notes || "-"}</div>
											<div className="col-controls">
												{(isBatchShipment ? shipmentDetails?.batchStatus : shipmentDetails?.status) == "Reconciling" &&
													<Button onClick={() => handleRemove(item.id)} disabled={(isAdding || isRemovingId) ? true : false}>{isRemovingId == item.id ? "Removing..." : "Remove"}</Button>
												}
											</div>
										</div>
									))}
								</>
							) : (
								<div className="no-results">0 results</div>
							)}
						</div>
						<div className="footers">
							<div className="col-expense"><strong>Total:</strong></div>
							<div className="col-amount"><strong>{new Decimal(total ?? 0).toFixed(2)}</strong></div>
							<div className="col-type">{" "}</div>
							<div className="col-payee">{" "}</div>
							<div className='col-manifest-number'>{" "}</div>
							<div className="col-notes">{" "}</div>
							<div className="col-controls">{" "}</div>
						</div>
					</div>
					{/* <Json data={shipmentExpenses}></Json> */}
				</LocalizationProvider>
			</Spinner>
		</section>
	)
}

export default ShipmentExpensesForm;